/**
 * AIシミュレーター デバッグヘルパー
 *
 * ブラウザコンソールまたはテストで使用するためのヘルパー関数
 *
 * 使用例（ブラウザコンソール）:
 * ```javascript
 * import { runDebugSimulation, analyzeMagicUsage } from './engine/gameEngine/debugHelper';
 *
 * // デバッグシミュレーション実行
 * const result = await runDebugSimulation({ deckName: '魔法使いデッキ' });
 * console.log(result.aiDecisionsFormatted);
 *
 * // 魔法カード使用分析
 * analyzeMagicUsage(result);
 * ```
 */

import { simulateGameDebug } from './Simulator';
import type { SimulationDebugResult, AIDecisionAlternative } from './Simulator';
import { PREBUILT_DECKS } from '../../decks/prebuiltDecks';
import { loadCardsFromCSV } from '../../utils/cardManager';
import { createDeckFromPrebuilt } from '../../utils/helpers';
import type { CardData, CardWithId } from '../../types/card';
import type { AIDifficulty } from '../ai/strategies';

/** AIデバッグインターフェース */
interface AIDebugInterface {
  runDebugSimulation: typeof runDebugSimulation;
  analyzeMagicUsage: typeof analyzeMagicUsage;
  analyzeSkillUsage: typeof analyzeSkillUsage;
  analyzeSummonDecisions: typeof analyzeSummonDecisions;
  analyzeAttackDecisions: typeof analyzeAttackDecisions;
  showSummary: typeof showSummary;
  testMagicDeck: typeof testMagicDeck;
  listAvailableDecks: typeof listAvailableDecks;
  getCardPool: typeof getCardPool;
  getDeckByName: typeof getDeckByName;
}

/** グローバルWindowにaiDebugを追加 */
declare global {
  interface Window {
    aiDebug?: AIDebugInterface;
  }
}

/** デバッグシミュレーションオプション */
interface DebugSimulationOptions {
  deck1Name?: string;
  deck2Name?: string;
  p1Difficulty?: AIDifficulty;
  p2Difficulty?: AIDifficulty;
  maxTurns?: number;
}

/** 効果タイプ統計 */
interface EffectTypeStats {
  [key: string]: number;
}

/**
 * カードプールをロード
 */
let cachedCardPool: CardData[] | null = null;
export async function getCardPool(): Promise<CardData[]> {
  if (cachedCardPool) return cachedCardPool;
  cachedCardPool = await loadCardsFromCSV();
  return cachedCardPool;
}

/**
 * デッキ名からデッキを取得
 */
export function getDeckByName(deckName: string, cardPool: CardData[]): CardWithId[] {
  const prebuiltDeck = PREBUILT_DECKS[deckName as keyof typeof PREBUILT_DECKS];
  if (!prebuiltDeck) {
    const availableDecks = Object.keys(PREBUILT_DECKS);
    throw new Error(`デッキ「${deckName}」が見つかりません。利用可能: ${availableDecks.join(', ')}`);
  }
  // deckNameをそのまま渡す（createDeckFromPrebuiltはdeckIdを期待）
  return createDeckFromPrebuilt(deckName, cardPool);
}

/**
 * デバッグシミュレーションを実行
 */
export async function runDebugSimulation(options: DebugSimulationOptions = {}): Promise<SimulationDebugResult> {
  const {
    deck1Name = 'starter',
    deck2Name = 'random',
    p1Difficulty = 'hard',
    p2Difficulty = 'hard',
    maxTurns = 20,
  } = options;

  const cardPool = await getCardPool();

  // デッキを作成
  let deck1: CardWithId[];
  let deck2: CardWithId[];

  if (deck1Name === 'random') {
    // ランダムデッキ - uniqueIdを付与
    const monsters = cardPool.filter(c => c.type === 'monster').slice(0, 30);
    const magics = cardPool.filter(c => c.type === 'magic').slice(0, 10);
    deck1 = [...monsters, ...magics].map((c, i) => ({ ...c, uniqueId: `rand1_${i}` }));
  } else {
    deck1 = getDeckByName(deck1Name, cardPool);
  }

  if (deck2Name === 'random') {
    const monsters = cardPool.filter(c => c.type === 'monster').slice(0, 30);
    const magics = cardPool.filter(c => c.type === 'magic').slice(0, 10);
    deck2 = [...monsters, ...magics].map((c, i) => ({ ...c, uniqueId: `rand2_${i}` }));
  } else {
    deck2 = getDeckByName(deck2Name, cardPool);
  }

  console.log(`\n🎮 デバッグシミュレーション開始`);
  console.log(`P1: ${deck1Name} (${p1Difficulty})`);
  console.log(`P2: ${deck2Name} (${p2Difficulty})`);
  console.log(`最大ターン数: ${maxTurns}`);

  const result = simulateGameDebug({
    deck1,
    deck2,
    p1Difficulty,
    p2Difficulty,
    maxTurns,
  });

  console.log(`\n📊 結果:`);
  console.log(`勝者: P${result.winner || 'なし（引き分け）'}`);
  console.log(`ターン数: ${result.turns}`);
  console.log(`P1 Life: ${result.p1Life}`);
  console.log(`P2 Life: ${result.p2Life}`);
  console.log(`実行時間: ${result.duration}ms`);

  return result;
}

/**
 * 魔法カードの使用状況を分析
 */
export function analyzeMagicUsage(result: SimulationDebugResult): void {
  const magicDecisions = result.getMagicDecisions();

  console.log(`\n✨ 魔法カード使用分析 (全${magicDecisions.length}回の判断)`);
  console.log('='.repeat(60));

  const used = magicDecisions.filter(d => d.action === 'used');
  const skipped = magicDecisions.filter(d => d.action === 'skipped');

  console.log(`\n📗 使用した魔法カード (${used.length}回):`);
  used.forEach(d => {
    console.log(`  ターン${d.turn} P${d.player}: ${d.details.cardName}`);
    console.log(`    効果タイプ: ${d.details.effectType}`);
    console.log(`    理由: ${d.details.reason}`);
    console.log(`    状況: Life ${d.details.gameContext?.myLife} vs ${d.details.gameContext?.opponentLife}`);
    console.log(`    フィールド: ${d.details.gameContext?.myFieldCount}体 vs ${d.details.gameContext?.opponentFieldCount}体`);
  });

  console.log(`\n📕 使用しなかった魔法カード (${skipped.length}回):`);
  skipped.forEach(d => {
    console.log(`  ターン${d.turn} P${d.player}: 候補 ${d.details.alternatives?.length || 0}枚`);
    if (d.details.alternatives) {
      d.details.alternatives.forEach(alt => {
        console.log(`    - ${alt.name} (${alt.effectType})`);
      });
    }
    console.log(`    理由: ${d.details.strategyNote || d.details.reason}`);
  });

  // 統計
  console.log(`\n📈 統計:`);
  const effectTypes: EffectTypeStats = {};
  used.forEach(d => {
    const type = (d.details.effectType as string) || 'unknown';
    effectTypes[type] = (effectTypes[type] || 0) + 1;
  });
  console.log('使用した効果タイプ:', effectTypes);

  const skipReasons: EffectTypeStats = {};
  skipped.forEach(d => {
    const reason = (d.details.strategyNote as string) || (d.details.reason as string) || 'unknown';
    skipReasons[reason] = (skipReasons[reason] || 0) + 1;
  });
  console.log('スキップ理由:', skipReasons);
}

/**
 * スキル使用状況を分析
 */
export function analyzeSkillUsage(result: SimulationDebugResult): void {
  const skillDecisions = result.getSkillDecisions();

  console.log(`\n⚔️ スキル使用分析 (全${skillDecisions.length}回の判断)`);
  console.log('='.repeat(60));

  const executed = skillDecisions.filter(d => d.action === 'executed');
  const skipped = skillDecisions.filter(d => d.action === 'skipped');

  console.log(`\n📗 使用したスキル (${executed.length}回):`);
  executed.forEach(d => {
    console.log(`  ターン${d.turn} P${d.player}: ${d.details.cardName}`);
    console.log(`    タイプ: ${d.details.skillType === 'advanced' ? '上級技' : '基本技'}`);
    console.log(`    理由: ${d.details.reason}`);
  });

  console.log(`\n📕 使用しなかったスキル (${skipped.length}回):`);
  skipped.forEach(d => {
    console.log(`  ターン${d.turn} P${d.player}: 候補 ${d.details.alternatives?.length || 0}体`);
    if (d.details.alternatives) {
      d.details.alternatives.forEach(alt => {
        console.log(`    - ${alt.name} (${alt.type === 'advanced' ? '上級技' : '基本技'})`);
      });
    }
    console.log(`    理由: ${d.details.reason}`);
  });
}

/**
 * 召喚判断を分析
 */
export function analyzeSummonDecisions(result: SimulationDebugResult): void {
  const summonDecisions = result.getSummonDecisions();

  console.log(`\n🐉 召喚分析 (全${summonDecisions.length}回の判断)`);
  console.log('='.repeat(60));

  const executed = summonDecisions.filter(d => d.action === 'executed');
  const skipped = summonDecisions.filter(d => d.action === 'skipped');

  console.log(`\n📗 召喚したモンスター (${executed.length}回):`);
  executed.forEach(d => {
    console.log(`  ターン${d.turn} P${d.player}: ${d.details.cardName}`);
    console.log(`    コスト: ${d.details.cost}, ATK: ${d.details.attack}, HP: ${d.details.hp}`);
    console.log(`    スロット: ${d.details.slot}`);
    if ((d.details.alternatives?.length ?? 0) > 1) {
      console.log(`    他の候補: ${d.details.alternatives?.filter(n => n.name !== d.details.cardName).map(n => n.name).join(', ')}`);
    }
  });

  console.log(`\n📕 召喚しなかった (${skipped.length}回):`);
  skipped.forEach(d => {
    console.log(`  ターン${d.turn} P${d.player}: 候補 ${d.details.alternatives?.length || 0}体`);
    if (d.details.alternatives) {
      d.details.alternatives.slice(0, 3).forEach(alt => {
        console.log(`    - ${alt.name} (コスト${alt.cost})`);
      });
    }
    console.log(`    SP: ${d.details.activeSP}`);
    console.log(`    理由: ${d.details.reason}`);
  });
}

/**
 * 攻撃判断を分析
 */
export function analyzeAttackDecisions(result: SimulationDebugResult): void {
  const attackDecisions = result.getAttackDecisions();

  console.log(`\n⚔️ 攻撃分析 (全${attackDecisions.length}回の判断)`);
  console.log('='.repeat(60));

  const executed = attackDecisions.filter(d => d.action === 'executed');
  const direct = executed.filter(d => d.details.targetIndex === -1);
  const monster = executed.filter(d => d.details.targetIndex !== -1);

  console.log(`\n直接攻撃: ${direct.length}回`);
  console.log(`モンスター攻撃: ${monster.length}回`);

  console.log(`\n詳細:`);
  executed.forEach(d => {
    console.log(`  ターン${d.turn} P${d.player}: ${d.details.attackerName} (ATK${d.details.attackerAttack})`);
    console.log(`    → ${d.details.targetName}`);
    console.log(`    理由: ${d.details.reason}`);
  });
}

/**
 * 全体サマリーを表示
 */
export function showSummary(result: SimulationDebugResult): void {
  console.log(`\n📋 AI思考サマリー`);
  console.log('='.repeat(60));
  console.log(result.aiDecisionsSummary);

  console.log(`\n📝 詳細ログ:`);
  console.log(result.aiDecisionsFormatted);
}

/**
 * 魔法デッキを使ったテスト
 * 魔法カードの使用タイミングをチェック
 */
export async function testMagicDeck(): Promise<SimulationDebugResult> {
  console.log('🧙 魔法デッキテスト開始');

  // 魔法カードが含まれるデッキを使用
  const result = await runDebugSimulation({
    deck1Name: 'water',          // 水属性デッキ
    deck2Name: 'starter',        // 炎ドラゴンスターターデッキ
    p1Difficulty: 'hard',
    p2Difficulty: 'hard',
    maxTurns: 15,
  });

  analyzeMagicUsage(result);

  return result;
}

/**
 * 利用可能なデッキ一覧を表示
 */
export function listAvailableDecks(): void {
  console.log('\n📚 利用可能なデッキ:');
  console.log('='.repeat(60));

  Object.entries(PREBUILT_DECKS).forEach(([key, deck]) => {
    console.log(`  ${key}: ${deck.name}`);
    console.log(`    属性: ${(deck as { attribute?: string }).attribute || '混合'}`);
    console.log(`    カード数: ${deck.cards?.length || 0}枚`);
  });
}

// グローバルにエクスポート（ブラウザコンソールで使用するため）
if (typeof window !== 'undefined') {
  window.aiDebug = {
    runDebugSimulation,
    analyzeMagicUsage,
    analyzeSkillUsage,
    analyzeSummonDecisions,
    analyzeAttackDecisions,
    showSummary,
    testMagicDeck,
    listAvailableDecks,
    getCardPool,
    getDeckByName,
  };
  console.log('🔧 AI Debug Helper loaded. Use window.aiDebug.* to access functions.');
}
